import React, { useMemo, useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Upload, Trash2 } from "lucide-react";
import { FormModal, Field, FormSubmit, yupResolver } from "../form";
import axiosInstance from "../../services/api/axios";
import { convertFilesToBase64 } from "../../utils/fileUtils";
import { phaseSchema, PHASE_STATUS_OPTIONS } from "../../services/projects/schemas";

/**
 * Create/edit a project phase. Mostly Form-driven, except for the multi-file
 * image picker — that lives in component state because Field doesn't yet
 * support multi-file with previews (one-off pattern, kept inline).
 *
 * The BE expects field names in snake_case via multipart FormData. The form
 * state stays in camelCase to match the rest of the app's conventions; the
 * snake_case translation happens at submit time.
 */
const PhaseModal = ({ isOpen, onClose, phase = null, projectId, onSuccess }) => {
    const isEdit = !!phase;
    const [imageFiles, setImageFiles] = useState([]);
    const [previewImages, setPreviewImages] = useState([]);

    const defaultValues = useMemo(
        () => ({
            name: phase?.name || "",
            reraNo: phase?.reraNo || "",
            reraStartDate: phase?.reraStartDate ? phase.reraStartDate.split("T")[0] : "",
            reraEndDate: phase?.reraEndDate ? phase.reraEndDate.split("T")[0] : "",
            towers: phase?.towers ?? "",
            units: phase?.units ?? "",
            configuration: phase?.configuration ? JSON.stringify(phase.configuration) : "",
            phaseStatus: phase?.phaseStatus || 1,
        }),
        [phase],
    );

    const methods = useForm({
        defaultValues,
        // onChange so `formState.isValid` stays reactive — the submit button
        // is hard-disabled until every required field is valid, so an
        // incomplete phase can't be submitted.
        mode: "onChange",
        resolver: yupResolver(phaseSchema),
    });

    // Reset on phase change (edit vs new vs different existing phase). Without
    // this RHF holds stale defaults across consecutive opens of the modal.
    useEffect(() => {
        if (isOpen) {
            methods.reset(defaultValues);
            setImageFiles([]);
            setPreviewImages(phase?.images?.length ? phase.images : []);
            // Seed validity immediately on open so the submit button reflects
            // the real state: disabled for a blank new phase, enabled for an
            // already-valid edit — without waiting for the first interaction.
            methods.trigger();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, phase]);

    const handleImageChange = async (e) => {
        e.stopPropagation();
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        try {
            const base64Images = await convertFilesToBase64(files);
            setImageFiles((prev) => [...prev, ...files]);
            setPreviewImages((prev) => [...prev, ...base64Images]);
        } catch (error) {
            console.error("Error processing images:", error);
            toast.error("Error processing images");
        }
    };

    const removeImage = (index) => {
        setImageFiles((prev) => prev.filter((_, i) => i !== index));
        setPreviewImages((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (data) => {
        try {
            const fd = new FormData();
            fd.append("project_id", projectId);
            fd.append("name", data.name);
            fd.append("rera_no", data.reraNo || "");
            fd.append("rera_start_date", data.reraStartDate || "");
            fd.append("rera_end_date", data.reraEndDate || "");
            fd.append("towers", data.towers);
            fd.append("units", data.units);
            fd.append("configuration", data.configuration || "");
            fd.append("phase_status", data.phaseStatus);
            imageFiles.forEach((file) => fd.append("images", file));
            if (isEdit) fd.append("project_phase_id", phase.projectPhaseId);

            const method = isEdit ? "put" : "post";
            const response = await axiosInstance[method]("/projects/phase", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            toast.success(isEdit ? "Phase updated successfully!" : "Phase created successfully!");
            // The axios interceptor unwraps `data` for us, but some callers might
            // see response.data — support both shapes for onSuccess consumers.
            const payload = response?.data != null ? response.data : response;
            onSuccess?.(payload);
            onClose();
        } catch (error) {
            console.error("Error saving phase:", error);
            toast.error(
                error?.detail ||
                    error?.response?.data?.detail ||
                    error?.response?.data?.message ||
                    "Error saving phase",
            );
        }
    };

    return (
        <FormModal
            open={isOpen}
            onClose={onClose}
            title={isEdit ? "Edit Phase" : "Add New Phase"}
            size="lg"
        >
            <FormProvider {...methods}>
                <form
                    onSubmit={methods.handleSubmit(handleSubmit)}
                    className="space-y-4"
                    noValidate
                >
                    <Field name="name" label="Phase Name" placeholder="Enter phase name" required />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Field name="reraNo" label="RERA Number" placeholder="RERA number" />
                        <Field name="reraStartDate" label="RERA Start Date" type="date" />
                        <Field name="reraEndDate" label="RERA End Date" type="date" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field
                            name="towers"
                            label="Number of Towers"
                            type="number"
                            min={0}
                            placeholder="0"
                            required
                        />
                        <Field
                            name="units"
                            label="Number of Units"
                            type="number"
                            min={0}
                            placeholder="0"
                            required
                        />
                    </div>

                    <div className="w-full md:w-1/2">
                        <Field
                            name="phaseStatus"
                            label="Phase Status"
                            type="select"
                            options={PHASE_STATUS_OPTIONS}
                            required
                        />
                    </div>

                    {/* Multi-file image picker — left bespoke until Field gains
                        a multi-file type. Files live in component state; the
                        submit handler appends them to the multipart FormData. */}
                    <div className="w-full">
                        <label className="block text-sm font-normal text-slate-500 mb-1">
                            Phase Images
                        </label>
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <Upload className="w-8 h-8 mb-2 text-gray-400" />
                                <p className="text-sm text-gray-500">
                                    <span className="font-semibold">Click to upload</span> or drag and drop
                                </p>
                                <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                            </div>
                            <input
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={handleImageChange}
                                onClick={(e) => e.stopPropagation()}
                                className="hidden"
                            />
                        </label>

                        {previewImages.length > 0 && (
                            <div className="mt-3">
                                <label className="block text-xs font-medium text-gray-500 mb-2">
                                    Selected Images ({previewImages.length})
                                </label>
                                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                                    {previewImages.map((image, index) => (
                                        <div key={index} className="relative group">
                                            <img
                                                src={image.base64 || image}
                                                alt={`Preview ${index + 1}`}
                                                className="w-full h-20 object-cover rounded border"
                                            />
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeImage(index);
                                                }}
                                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <FormSubmit
                        loadingLabel={isEdit ? "Updating..." : "Creating..."}
                        disableWhenInvalid
                    >
                        {isEdit ? "Update Phase" : "Create Phase"}
                    </FormSubmit>
                </form>
            </FormProvider>
        </FormModal>
    );
};

export default PhaseModal;
